import React, { useContext, useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import SessionContext from '../../contexts/SessionContext';
import ProductContext from '../../contexts/ProductContext';
import { useAddToCartMutation } from '../../redux-modules/service';
import { ArrowRight, Cart, Cross } from '../../components/ui/Icons';

export default function AddToCart({
  selectedVariant,
}: {
  selectedVariant: number;
}) {
  const [handleAddToCart, {
    isSuccess,
    isError,
    error,
    data,
  }] = useAddToCartMutation();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { translates } = useContext(ProductContext);
  const { products } = translates;
  let lineItem = null;

  const getErrorMessage = (errorData: any) => {
    if (errorData && 'data' in errorData && errorData.data?.errors) {
      const errors = errorData.data.errors as Record<string, string[]>;
      return Object.values(errors)[0][0];
    }
    return 'Er is een fout opgetreden';
  };

  useEffect(() => {
    setShowDialog(isSuccess || isError);
  }, [isSuccess, isError]);

  if (isSuccess) {
    htmx.trigger('#header-btns', 'refresh');
  }

  if (isError) {
    const errorMessage = getErrorMessage(error);
    const errorMessageInput = document.getElementById('notification-error');
    errorMessageInput?.setAttribute('value', errorMessage);
    htmx.trigger('#notification', 'refresh');
  }

  if (isSuccess && data) {
    lineItem = data.cart.lineItems.find((o: any) => o.purchasableId === selectedVariant);
  }

  const session = useContext(SessionContext);
  const { csrfTokenName, csrfTokenValue } = session;

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary flex-1.0 justify-between"
        onClick={() => {
          const body = new FormData();
          if (csrfTokenName && csrfTokenValue) {
            body.append(csrfTokenName, csrfTokenValue);
          }
          body.append('purchasableId', selectedVariant.toString());
          handleAddToCart(body);
        }}
      >
        {Craft.t(products, 'In winkelmand')}
        <Cart />
      </button>
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        className="relative z-[9999]"
      >
        <DialogBackdrop className="fixed inset-0 bg-near-black/50" />
        <div className="fixed flex inset-0 items-center h-svh overflow-y-auto">
          <DialogPanel className="bg-white rounded-lg shadow-md-secondary w-full md:pt-8 md:pb-12 p-4 md:px-12 text-sm md:text-base mx-auto my-4 md:my-8 max-w-modal-sm">
            <div className="relative flex gap-4 pb-4 md:pb-7.5 mb-4 md:mb-7.5 border-b border-light-grey-1.5">
              <h3 className="text-xl md:text-2xl-secondary leading-tight flex-1.0">
                {isError ? Craft.t(products, 'Error') : Craft.t(products, 'Toegevoegd aan je winkelmand')}
              </h3>
              <button
                className="btn-close absolute top-0 right-0 bottom-0 w-8 md:w-12 h-8 md:h-12 p-2 md:p-3 flex items-center justify-center transition-colors text-near-black  hover:text-accent"
                type="button"
                onClick={() => setShowDialog(false)}
              >
                <span className="sr-only">Close modal</span>
                <Cross className="w-full h-full" />
              </button>
            </div>
            {isError ? (
              <div className="text-red-600 mb-4">
                {getErrorMessage(error)}
              </div>
            ) : (
              lineItem && (
                <div className="flex">
                  <img src={lineItem.snapshot.image} alt={lineItem.description} className="aspect-square" width="94" height="94" />
                  <div className="flex w-full mx-4 items-start justify-between">
                    <div className="flex flex-1.0 flex-col">
                      <h4 className="font-semibold text-base mb-0.5">{lineItem.description}</h4>
                      <div className="mt-1 flex flex-col text-sm *:mb-1">
                        <p className="text-gray-500">{lineItem.sku}</p>
                        {lineItem.snapshot.color && (
                          <p className="text-gray-500">
                            {lineItem.snapshot.color}
                          </p>
                        )}
                        {lineItem.snapshot.size && (
                          <p className="text-gray-500">
                            {lineItem.snapshot.size}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col justify-start items-center m-0 pl-2.5 text-right whitespace-nowrap w-auto">
                      {lineItem.onPromotion && (
                        <span className="text-sm font-normal text-gray-400"><s>{lineItem.originalTotalAsCurrency}</s></span>
                      )}
                      <span className="text-base font-semibold">{lineItem.totalAsCurrency}</span>
                    </div>
                  </div>
                </div>
              )
            )}
            <div className="my-4">
              {!isError && (
                <a href={Craft.getSiteUrl('winkelmand')} className="btn btn-secondary w-full">
                  {Craft.t(products, 'Winkelmand bekijken')}
                </a>
              )}
            </div>
            <div className="flex">
              <button
                type="button"
                className="btn btn-link btn-link-sm mx-auto"
                onClick={() => setShowDialog(false)}
              >
                {Craft.t(products, 'Verder winkelen')}
                <ArrowRight />
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
